export type TypeOfObjectValues<T extends Record<any, any>> = {
  [X in keyof T]: T[X];
}[keyof T];

export type StdFunction = (...params: any[]) => void;

export interface StdInterface {
  warn: StdFunction;
  error: StdFunction;
  log: StdFunction;
  trace: StdFunction;
}

type StdForPlugin = (...args: any[]) => unknown;

export type Plugin = {
  message?: (...args: any[]) => any[];
  log?: StdForPlugin;
  warn?: StdForPlugin;
  trace?: StdForPlugin;
  error?: StdForPlugin;
};

export type LogLevelValue = LogLevelType | (() => LogLevelType);
export type ProcessMsg = (...args: any[]) => string;

export interface WarnerConfigs {
  std: StdInterface;
  logLevel?: LogLevelValue;
  plugins?: Plugin[];
  prefix: string;
}

export const LogLevel = {
  DEBUG: 0,
  LOG: 1,
  WARN: 2,
  ERROR: 3,
  NOTHING: 4,
};

type LogLevelType = TypeOfObjectValues<typeof LogLevel>;

function getLogLevel(logLevel?: LogLevelValue) {
  return typeof logLevel === 'function' ? logLevel() : logLevel;
}

function findLastPluginHasHook(plugins: Plugin[], hook: keyof Plugin) {
  const reversed = [...plugins].reverse();

  return reversed.find((plugin) => hook in plugin);
}

export function createSystemLogger({ std, logLevel: _logLevel, plugins = [], prefix }: WarnerConfigs) {
  let logLevel: LogLevelType = getLogLevel(_logLevel) || LogLevel.LOG;

  function getMsg(msg: any[]) {
    const hook = findLastPluginHasHook(plugins, 'message');

    return [`[${prefix}]`, ...(hook ? hook.message!(...msg) : msg)];
  }

  function runThroughPlugins(lifeCycle: keyof Plugin, ...args: any[]) {
    plugins.forEach((plugin) => {
      plugin[lifeCycle]?.(`[${prefix}]`, ...args);
    });
  }

  function call(std: StdFunction, msg: any[], stdLevel: LogLevelType) {
    if (stdLevel < logLevel) {
      return;
    }

    return std(...getMsg(msg));
  }

  return {
    getLogLevel: () => {
      return logLevel;
    },

    setLogLevel: (_logLevel: LogLevelType) => {
      logLevel = _logLevel;
    },

    warn: (...warningMsg: any[]) => {
      runThroughPlugins('warn', ...warningMsg);
      return call(std.warn, warningMsg, LogLevel.WARN);
    },

    log: (...logMsg: any[]) => {
      runThroughPlugins('log', ...logMsg);
      return call(std.log, logMsg, LogLevel.LOG);
    },

    error: (...errorMsg: any[]) => {
      runThroughPlugins('error', ...errorMsg);
      return call(std.error, errorMsg, LogLevel.ERROR);
    },
  };
}
